import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  EditProps,
  Toolbar,
  useEditController,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import { GetParam } from "utils/tools";
import SubcontractorRateChartAreaDatagrid from "./components/SubcontractorRateChartAreaDatagrid";
import CapacityIncentiveRateChartAreaDatagrid from "./components/CapacityIncentiveRateChartAreaDatagrid";
import SubcontractorRateChartAreaDatagridSchoolIncentive from "./components/SubcontractorRateChartAreaDatagridSchoolIncentive";
import SaveButtonSubcontractorRateChart from "./components/SaveButtonSubcontractorRateChart";
import CustomNumberInput from "../../components/CustomNumberInput";
import PermissionLoading from "../../components/PermissionLoading";
import EditWrapper from "../../components/EditWrapper";

const useStyles = makeStyles({
  additionalIncentive: {
    marginLeft: "8px",
  },
});

const SubcontractorRateChartsEdit = (props: EditProps) => {
  const [areas, setAreas] = useState([]);
  const param = GetParam();
  const provider = useDataProvider();
  const { record, loading } = useEditController(props);

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    {
      title: "Subcontractor Pricing",
      link: `/schools/${param.get("schoolId")}/show/6`,
    },
    { title: "Edit", link: "" },
  ];

  const classes = useStyles();

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("subcontractorAreas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result) => {
        result.data
          .sort((a: any, b: any) => {
            return a.maxDistanceInMeters - b.maxDistanceInMeters;
          })
          .map((record: any, index: number) => {
            const item = record;
            if (item) {
              item.order = index + 1;
            }
          });

        setAreas(result.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CustomToolbar = (props: any) => {
    return (
      <Toolbar {...props}>
        <SaveButtonSubcontractorRateChart formInvalid={props.invalid} />
      </Toolbar>
    );
  };

  const formatRecord = (res: any) => {
    if (res) {
      if (res.busAssistantIncentive) {
        res.busAssistantIncentiveFee =
          res.busAssistantIncentive.toFixed(2) / 100;
      }

      if (res.additionalIncentiveRates.smallVehicleFee) {
        res.additionalIncentiveRates.smallVehicleFeeValue =
          res.additionalIncentiveRates.smallVehicleFee.toFixed(2) / 100;
      }
      if (res.additionalIncentiveRates.mediumVehicleFee) {
        res.additionalIncentiveRates.mediumVehicleFeeValue =
          res.additionalIncentiveRates.mediumVehicleFee.toFixed(2) / 100;
      }

      res.regularItemsToSend = res.items.filter(
        (item: any) => item.rateChartItemType === "regular"
      );
      res.ccaItemsToSend = res.items.filter(
        (item: any) => item.rateChartItemType === "cca"
      );

      return res;
    }
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper breadcrumb={breadcrumb} actions={null} {...props}>
        <SimpleForm record={formatRecord(record)} toolbar={<CustomToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomNumberInput
                source="busAssistantIncentiveFee"
                label="Bus Assistant Incentive ($)"
                isRequired
                isPositiveValue
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Area Pricing for Regular</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagrid
                areas={areas}
                edit={true}
                type="regular"
                itemsToSendField="regularItemsToSend"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Area Pricing for CCA</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagrid
                areas={areas}
                edit={true}
                type="cca"
                itemsToSendField="ccaItemsToSend"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Incentive Threshold</Typography>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Additional Incentive</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="smallVehicleAdditionalIncentiveThreshold"
                  label="Small Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="mediumVehicleAdditionalIncentiveThreshold"
                  label="Medium Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>
            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Capacity Incentive</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="mediumVehicleCapacityIncentiveThreshold"
                  label="Medium Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="largeVehicleCapacityIncentiveThreshold"
                  label="Large Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Additional Incentive Rates</Typography>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Small Vehicle</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="additionalIncentiveRates.smallVehicleFeeValue"
                  label="Fee ($)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Medium Vehicle</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="additionalIncentiveRates.mediumVehicleFeeValue"
                  label="Fee ($)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Capacity Incentive Rates</Typography>
            </Grid>

            <Grid item xs={6}>
              <CapacityIncentiveRateChartAreaDatagrid
                areas={areas}
                edit={true}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Incentive Pricing</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagridSchoolIncentive
                areas={areas}
                edit={true}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SubcontractorRateChartsEdit;
